import { MediaBlock } from '@/blocks/MediaBlock/Component'
import { DefaultNodeTypes, SerializedBlockNode, SerializedLinkNode, type DefaultTypedEditorState } from '@payloadcms/richtext-lexical'
import { JSXConvertersFunction, LinkJSXConverter, RichText as ConvertRichText } from '@payloadcms/richtext-lexical/react'

import { CodeBlock, CodeBlockProps } from '@/blocks/Code/Component'

import type { BannerBlock as BannerBlockProps, CallToActionBlock as CTABlockProps, MediaBlock as MediaBlockProps } from '@/payload-types'
import { BannerBlock } from '@/blocks/Banner/Component'
import { CallToActionBlock } from '@/blocks/CallToAction/Component'
import { cn } from '@/utilities/ui'
import { DataWrapperBlock, type DataWrapperProps } from '@/blocks/DataWrapper/Component'
import { IFrameBlock, type IFrameProps } from '@/blocks/IFrame/Component'
import { EmbedVideoBlock, type EmbedVideoProps } from '@/blocks/EmbedVideo/Component'
import { EmbedPostBlock, type EmbedPostProps } from '@/blocks/EmbedPost/Component'
import { EmbedAudioBlock, type EmbedAudioProps } from '@/blocks/EmbedAudio/Component'
import { LinkButtonBlock, type LinkButtonProps } from '@/blocks/LinkButton/Component'

type NodeTypes =
    | DefaultNodeTypes
    | SerializedBlockNode<
          | CTABlockProps
          | MediaBlockProps
          | BannerBlockProps
          | CodeBlockProps
          | DataWrapperProps
          | IFrameProps
          | EmbedVideoProps
          | EmbedPostProps
          | EmbedAudioProps
          | LinkButtonProps
      >

const internalDocToHref = ({ linkNode }: { linkNode: SerializedLinkNode }) => {
    const { value, relationTo } = linkNode.fields.doc!
    if (typeof value !== 'object') {
        throw new Error('Expected value to be an object')
    }
    const slug = value.slug
    return relationTo === 'posts' ? `/posts/${slug}` : `/${slug}`
}

const jsxConverters: JSXConvertersFunction<NodeTypes> = ({ defaultConverters }) => ({
    ...defaultConverters,
    ...LinkJSXConverter({ internalDocToHref }),
    blocks: {
        banner: ({ node }) => <BannerBlock className='col-start-2 mb-4' {...node.fields} />,
        mediaBlock: ({ node }) => (
            <MediaBlock
                className='col-start-1 col-span-3'
                imgClassName='m-0 w-full'
                {...node.fields}
                captionClassName='mx-auto max-w-[48rem] [&_p]:text-sm -mt-6 font-sans'
                enableGutter={false}
                disableInnerContainer={true}
            />
        ),
        code: ({ node }) => <CodeBlock className='col-start-2' {...node.fields} />,
        cta: ({ node }) => <CallToActionBlock {...node.fields} />,
        datawrapper: ({ node }) => <DataWrapperBlock {...node.fields} />,
        iframe: ({ node }) => <IFrameBlock {...node.fields} />,
        embedVideo: ({ node }) => <EmbedVideoBlock {...node.fields} />,
        embedPost: ({ node }) => <EmbedPostBlock {...node.fields} />,
        embedAudio: ({ node }) => <EmbedAudioBlock {...node.fields} />,
        linkButton: ({ node }) => <LinkButtonBlock {...node.fields} />,
    },
})

const proseLinkClasses =
    'prose-a:no-underline prose-a:font-normal hover:prose-a:text-accent prose-a:shadow-[0_-2px_0_inset] prose-a:shadow-accent prose-a:transition-colors prose-a:duration-100 prose-a:ease-out'

type Props = {
    data: DefaultTypedEditorState
    enableGutter?: boolean
    enableProse?: boolean
} & React.HTMLAttributes<HTMLDivElement>

export default function RichText(props: Props) {
    const { className, enableProse = true, enableGutter = true, ...rest } = props
    const proseClasses = `mx-auto prose md:prose-md dark:prose-invert ${proseLinkClasses}`

    return (
        <ConvertRichText
            converters={jsxConverters}
            className={cn(
                'payload-richtext',
                {
                    container: enableGutter,
                    'max-w-none': !enableGutter,
                    [proseClasses]: enableProse,
                },
                className,
            )}
            {...rest}
        />
    )
}
